import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, hasRole } from "@/lib/auth";
import AppShell from "@/components/layout/AppShell";
import { LoadingState } from "@/components/common/UI";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Plants from "@/pages/foundation/Plants";
import SteelGrades from "@/pages/foundation/SteelGrades";
import ScrapRates from "@/pages/foundation/ScrapRates";
import Machines from "@/pages/foundation/Machines";
import MachineRates from "@/pages/foundation/MachineRates";
import ProcessRates from "@/pages/foundation/ProcessRates";
import ConversionOperations from "@/pages/foundation/ConversionOperations";
import Customers from "@/pages/foundation/Customers";
import Suppliers from "@/pages/foundation/Suppliers";
import BOPCatalogue from "@/pages/foundation/BOPCatalogue";
import { ZBC as ZBCStub } from "@/pages/Stubs";
import SupplierQuotes from "@/pages/SupplierQuotes";
import RFQList from "@/pages/rfq/RFQList";
import RFQForm from "@/pages/rfq/RFQForm";
import RFQDetail from "@/pages/rfq/RFQDetail";
import PendingApproval from "@/pages/rfq/PendingApproval";
import TataDemoView from "@/pages/zbc/TataDemoView";
import SettlementList from "@/pages/settlement/SettlementList";
import SettlementForm from "@/pages/settlement/SettlementForm";
import SettlementDetail from "@/pages/settlement/SettlementDetail";
import SteelPriceHistory from "@/pages/settlement/SteelPriceHistory";
import Reports from "@/pages/Reports";
import ZBCList from "@/pages/zbc/ZBCList";
import ZBCForm from "@/pages/zbc/ZBCForm";
import AIZBCWizard from "@/pages/zbc/AIZBCWizard";
import SupplierCompare from "@/pages/zbc/SupplierCompare";
import ZBCDetail from "@/pages/zbc/ZBCDetail";
import VarianceReport from "@/pages/zbc/VarianceReport";
import Users from "@/pages/UserManagement";
import AuditLog from "@/pages/AuditLog";
import UserManual from "@/pages/UserManual";
import FormulaRegistry from "@/pages/FormulaRegistry";

function Protected({ children, roles }) {
    const { user } = useAuth();
    if (user === undefined) {
        return <div className="min-h-screen flex items-center justify-center"><LoadingState label="Loading" /></div>;
    }
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !hasRole(user, ...roles)) {
        return (
            <AppShell>
                <div className="max-w-md mx-auto text-center py-16">
                    <h2 className="font-display text-2xl font-semibold text-slate-900 mb-2">Access denied</h2>
                    <p className="text-sm text-slate-500">Your role <span className="font-semibold">{user.role}</span> doesn&apos;t have access to this page.</p>
                </div>
            </AppShell>
        );
    }
    return <AppShell>{children}</AppShell>;
}

const ADMIN = ["CORPORATE_ADMIN"];
const ADMIN_FINANCE = ["CORPORATE_ADMIN", "FINANCE_PRICING"];
const ADMIN_FINANCE_DH = ["CORPORATE_ADMIN", "FINANCE_PRICING", "DEPT_HEAD"];
const ADMIN_FINANCE_DIR = ["CORPORATE_ADMIN", "FINANCE_PRICING", "DIRECTOR"];
const ADMIN_PLANT = ["CORPORATE_ADMIN", "PLANT_MANAGER"];
const ADMIN_PURCHASE = ["CORPORATE_ADMIN", "PURCHASE_MANAGER"];
const ADMIN_DIR = ["CORPORATE_ADMIN", "DIRECTOR"];

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />

            <Route path="/foundation/plants" element={<Protected roles={ADMIN}><Plants /></Protected>} />
            <Route path="/foundation/steel-grades" element={<Protected roles={ADMIN}><SteelGrades /></Protected>} />
            <Route path="/foundation/scrap-rates" element={<Protected roles={ADMIN}><ScrapRates /></Protected>} />
            <Route path="/foundation/machines" element={<Protected roles={ADMIN_PLANT}><Machines /></Protected>} />
            <Route path="/foundation/machine-rates" element={<Protected roles={ADMIN_PLANT}><MachineRates /></Protected>} />
            <Route path="/foundation/process-rates" element={<Protected roles={ADMIN}><ProcessRates /></Protected>} />
            <Route path="/foundation/conversion-operations" element={<Protected roles={ADMIN}><ConversionOperations /></Protected>} />
            <Route path="/foundation/customers" element={<Protected roles={ADMIN}><Customers /></Protected>} />
            <Route path="/foundation/suppliers" element={<Protected roles={ADMIN}><Suppliers /></Protected>} />
            <Route path="/foundation/bop-catalogue" element={<Protected roles={ADMIN}><BOPCatalogue /></Protected>} />

            <Route path="/zbc" element={<Protected roles={ADMIN_FINANCE}><ZBCList /></Protected>} />
            <Route path="/zbc/new" element={<Protected roles={ADMIN_FINANCE}><ZBCForm /></Protected>} />
            <Route path="/zbc/ai-new" element={<Protected roles={ADMIN_FINANCE}><AIZBCWizard /></Protected>} />
            <Route path="/zbc/supplier-compare" element={<Protected roles={ADMIN_FINANCE}><SupplierCompare /></Protected>} />
            <Route path="/zbc/variance-report" element={<Protected roles={ADMIN_FINANCE_DH}><VarianceReport /></Protected>} />
            <Route path="/zbc/demo/tata" element={<Protected roles={ADMIN_FINANCE_DIR}><TataDemoView /></Protected>} />
            <Route path="/zbc/:id" element={<Protected roles={ADMIN_FINANCE_DH}><ZBCDetail /></Protected>} />
            <Route path="/zbc/:id/edit" element={<Protected roles={ADMIN_FINANCE}><ZBCForm /></Protected>} />
            <Route path="/rfq" element={<Protected roles={ADMIN_FINANCE_DH}><RFQList /></Protected>} />
            <Route path="/rfq/new" element={<Protected roles={ADMIN_FINANCE}><RFQForm /></Protected>} />
            <Route path="/rfq/pending-approval" element={<Protected roles={["CORPORATE_ADMIN","DEPT_HEAD","DIRECTOR"]}><PendingApproval /></Protected>} />
            <Route path="/rfq/:id" element={<Protected roles={ADMIN_FINANCE_DH.concat(["DIRECTOR"])}><RFQDetail /></Protected>} />
            <Route path="/rfq/:id/edit" element={<Protected roles={ADMIN_FINANCE}><RFQForm /></Protected>} />
            <Route path="/settlement" element={<Protected roles={ADMIN_FINANCE_DIR}><SettlementList /></Protected>} />
            <Route path="/settlement/new" element={<Protected roles={ADMIN_FINANCE}><SettlementForm /></Protected>} />
            <Route path="/settlement/:id" element={<Protected roles={ADMIN_FINANCE_DIR}><SettlementDetail /></Protected>} />
            <Route path="/settlement/:id/edit" element={<Protected roles={ADMIN_FINANCE}><SettlementForm /></Protected>} />
            <Route path="/steel-price-history" element={<Protected roles={ADMIN_FINANCE_DIR}><SteelPriceHistory /></Protected>} />
            <Route path="/reports" element={<Protected roles={ADMIN_FINANCE_DH.concat(["DIRECTOR"])}><Reports /></Protected>} />
            <Route path="/supplier-quotes" element={<Protected roles={ADMIN_PURCHASE}><SupplierQuotes /></Protected>} />

            <Route path="/users" element={<Protected roles={ADMIN}><Users /></Protected>} />
            <Route path="/audit-log" element={<Protected roles={ADMIN_DIR}><AuditLog /></Protected>} />
            <Route path="/settings" element={<Protected roles={ADMIN}><div className="text-slate-500">Settings page (coming soon)</div></Protected>} />
            <Route path="/user-manual" element={<Protected><UserManual /></Protected>} />
            <Route path="/formulas" element={<Protected roles={ADMIN_FINANCE}><FormulaRegistry /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster
                    position="bottom-right"
                    toastOptions={{
                        className: "rounded-lg border border-slate-200 shadow-lg",
                        style: {
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: "13px",
                        },
                    }}
                />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
